import {
  Button,
  FieldWrapper,
  Icons,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import {
  TWorkflowHelpResource,
  TWorkflowHelpResourceType,
} from '@commenda/types';
import { useWorkflow } from '../../context/WorkflowContext';
import { TSelectedEntity } from '../../types/node';

const HELP_RESOURCE_TYPE_OPTIONS: {
  label: string;
  value: TWorkflowHelpResourceType;
}[] = [{ label: 'Video', value: 'video' }];

export const HelpResourcesConfig = ({
  selectedEntity,
}: {
  selectedEntity: TSelectedEntity;
}) => {
  const { workflow, setWorkflow } = useWorkflow();

  const selectedStep = workflow?.steps.find(
    (step) => step.id === selectedEntity.id
  );

  if (!selectedStep) return null;

  const helpResources: TWorkflowHelpResource[] =
    selectedStep.helpResources ?? [];

  const updateHelpResources = (updated: TWorkflowHelpResource[]) => {
    setWorkflow({
      ...workflow,
      steps: workflow.steps.map((step) =>
        step.id === selectedEntity.id
          ? { ...step, helpResources: updated.length ? updated : undefined }
          : step
      ),
    });
  };

  const addResource = () => {
    updateHelpResources([
      ...helpResources,
      { type: 'video', url: '', label: undefined },
    ]);
  };

  const removeResource = (index: number) => {
    updateHelpResources(helpResources.filter((_, i) => i !== index));
  };

  const updateResource = (
    index: number,
    patch: Partial<TWorkflowHelpResource>
  ) => {
    updateHelpResources(
      helpResources.map((r, i) => (i === index ? { ...r, ...patch } : r))
    );
  };

  return (
    <FieldWrapper>
      <div className="flex items-center justify-between">
        <Label>Help Resources</Label>
        <Button variant="tertiary" size="sm" onClick={addResource}>
          <Icons.Plus className="size-4" />
          Add
        </Button>
      </div>

      {helpResources.length > 0 && (
        <div className="flex flex-col gap-3 mt-2">
          {helpResources.map((resource, index) => (
            <div
              key={resource.url}
              className="flex flex-col gap-2 p-3 border border-border-primary rounded-md"
            >
              <div className="flex items-center justify-between">
                <span className="txt-small-medium text-text-secondary">
                  Resource {index + 1}
                </span>
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={() => removeResource(index)}
                >
                  <Icons.Trash01 className="size-4 text-text-error" />
                </Button>
              </div>

              <div className="flex flex-col gap-1">
                <Label>Type</Label>
                <Select
                  value={resource.type}
                  onValueChange={(value) =>
                    updateResource(index, {
                      type: value as TWorkflowHelpResourceType,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {HELP_RESOURCE_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-1">
                <Label>URL</Label>
                <Input
                  value={resource.url}
                  placeholder="https://..."
                  onChange={(e) =>
                    updateResource(index, { url: e.target.value })
                  }
                />
              </div>

              <div className="flex flex-col gap-1">
                <Label>
                  Label{' '}
                  <span className="txt-extra-small-regular text-text-tertiary">
                    (optional)
                  </span>
                </Label>
                <Input
                  value={resource.label ?? ''}
                  placeholder="Watch video"
                  onChange={(e) =>
                    updateResource(index, {
                      label: e.target.value || undefined,
                    })
                  }
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </FieldWrapper>
  );
};
